import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage, t } from '../utils/i18n';
import client from '../utils/sanityClient';

const CatalogPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featuredProduct, setFeaturedProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { language } = useLanguage();
  
  // Load products and categories data from SANITY
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Fetch products
        const sanityProducts = await client.fetch(`
          *[_type == "product"] { 
            _id,
            name,
            "slug": slug.current,
            price,
            description,
            "category": category->name,
            images[]{ 
              _key,
              alt,
              principal,
              asset->{
                _id,
                url 
              }
            }
          }
        `);

        // Fetch categories
        const sanityCategories = await client.fetch(`
          *[_type == "category"] | order(name asc) {
            _id,
            name,
            "slug": slug.current
          }
        `);
        
        // Map Sanity products
        const mappedProducts = sanityProducts.map(product => {
          const mainImage = product.images?.find(img => img.principal) || product.images?.[0];
          return {
            id: product._id,
            name: product.name,
            slug: product.slug,
            image: mainImage?.asset?.url || '/assets/icons/placeholder.png',
            price: product.price || 0,
            material: product.description || 'Material no especificado',
            category: product.category,
            images: product.images || [],
            _original: product 
          };
        });

        setProducts(mappedProducts);

        // Determine a price metric (max price) per category and sort categories descending
        const priceLookup = mappedProducts.reduce((acc, prod) => {
          if (prod.category) {
            acc[prod.category] = Math.max(acc[prod.category] || 0, prod.price || 0);
          }
          return acc;
        }, {});

        const sortedCategories = (sanityCategories || []).sort((a, b) => {
          const priceA = priceLookup[a.name] || 0;
          const priceB = priceLookup[b.name] || 0;
          return priceB - priceA; // higher prices first
        });

        setCategories(sortedCategories);
        
        // Set a random featured product
        if (mappedProducts.length > 0) {
          const randomIndex = Math.floor(Math.random() * mappedProducts.length);
          setFeaturedProduct(mappedProducts[randomIndex]);
        }

      } catch (error) {
        console.error('Failed to load data from Sanity:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, []);

  // Get products by category
  const getProductsByCategory = (categoryName) => {
    return products.filter(product => product.category === categoryName).slice(0, 6);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(price);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F2EBE6] to-[#D5BCA3] flex items-center justify-center">
        <motion.div 
          className="text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="w-16 h-16 border-4 border-akasha-brown border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-akasha-brown text-lg">Cargando catálogo...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F2EBE6] to-[#D5BCA3]">
      {/* Hero Section with Featured Product */}
      {featuredProduct && (
        <motion.section 
          className="relative h-screen flex items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <div className="absolute inset-0 z-0">
            <img 
              src={featuredProduct.image} 
              alt={featuredProduct.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/40"></div>
          </div>
          
          <div className="relative z-10 container mx-auto px-4">
            <div className="max-w-2xl">
              <motion.div
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur-md rounded-full text-white/90 text-sm font-medium mb-4">
                  Producto Destacado
                </span>
                <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                  {featuredProduct.name}
                </h1>
                <p className="text-xl text-white/90 mb-4">
                  {featuredProduct.material}
                </p>
                <p className="text-3xl font-bold text-white mb-8">
                  {formatPrice(featuredProduct.price)}
                </p>
                <div className="flex gap-4">
                  <Link
                    to={`/product/${featuredProduct.slug || featuredProduct.id}`}
                    className="inline-flex items-center px-8 py-3 bg-white text-akasha-brown font-semibold rounded-full hover:bg-white/90 transition-all duration-300 hover:scale-105"
                  >
                    Ver Detalles
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                  <button 
                    onClick={() => setFeaturedProduct(products[Math.floor(Math.random() * products.length)])}
                    className="inline-flex items-center px-8 py-3 bg-white/20 backdrop-blur-md text-white font-semibold rounded-full border border-white/30 hover:bg-white/30 transition-all duration-300"
                  >
                    Otro Producto
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.section>
      )}

      {/* Category Showcase Sections */}
      <div className="py-20">
        {categories.map((category, categoryIndex) => {
          const categoryProducts = getProductsByCategory(category.name);
          
          if (categoryProducts.length === 0) return null;

          return (
            <motion.section 
              key={category._id}
              className="mb-32"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: categoryIndex * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="container mx-auto px-4">
                {/* Category Header */}
                <div className="text-center mb-16">
                  <h2 className="text-4xl md:text-5xl font-bold text-akasha-brown mb-4">
                    {category.name}
                  </h2>
                  <div className="w-24 h-1 bg-akasha-brown mx-auto"></div>
                </div>

                {/* Products Layout - Masonry style */}
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {categoryProducts.map((product, index) => (
                    <motion.div
                      key={product.id}
                      className={`group cursor-pointer ${
                        index === 0 || index === 1 ? 'md:col-span-2' : 
                        index === 4 ? 'md:col-span-2' : ''
                      }`}
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <Link to={`/product/${product.slug || product.id}`}>
                        <div className="relative overflow-hidden rounded-2xl bg-white/20 backdrop-blur-md border border-white/30 shadow-xl h-full">
                          <div className={`relative ${(index === 0 || index === 1) ? 'pb-[60%]' : 'pb-[120%]'}`}>
                            <img 
                              src={product.image} 
                              alt={product.name}
                              className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                          </div>
                          
                          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                            <div className="bg-black/40 backdrop-blur-sm rounded-xl p-4 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                              <h3 className={`font-bold mb-2 ${(index === 0 || index === 1) ? 'text-2xl' : 'text-lg'}`}>
                                {product.name}
                              </h3>
                              <p className="text-white/90 text-sm mb-3">
                                {product.material}
                              </p>
                              <div className="flex items-center justify-between">
                                <span className={`font-bold ${(index === 0 || index === 1) ? 'text-xl' : 'text-lg'}`}>
                                  {formatPrice(product.price)}
                                </span>
                                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>

                {/* View All Button */}
                <div className="text-center mt-12">
                  <Link
                    to={`/store?category=${category.name}`}
                    className="inline-flex items-center px-8 py-3 bg-akasha-brown text-white font-semibold rounded-full hover:bg-akasha-brown/90 transition-all duration-300 hover:scale-105"
                  >
                    Ver todos los productos de {category.name}
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                </div>
              </div>
            </motion.section>
          );
        })}
      </div>

      {/* Call to Action Section */}
      <motion.section 
        className="py-20 bg-akasha-brown/10 backdrop-blur-md"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-akasha-brown mb-6">
            ¿No encontraste lo que buscas?
          </h2>
          <p className="text-xl text-akasha-brown/80 mb-8 max-w-2xl mx-auto">
            Explora nuestra tienda completa con filtros avanzados o contáctanos para productos personalizados.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/store"
              className="inline-flex items-center px-8 py-3 bg-akasha-brown text-white font-semibold rounded-full hover:bg-akasha-brown/90 transition-all duration-300 hover:scale-105"
            >
              Explorar Tienda Completa
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-3 bg-white/20 backdrop-blur-md text-akasha-brown font-semibold rounded-full border border-akasha-brown/30 hover:bg-white/30 transition-all duration-300"
            >
              Contactar
            </Link>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default CatalogPage;
